import { ItemArt } from '@/shared/ui/item-art';
import { GameIcon } from '@/shared/ui/game-icon';
import { copperButton, metalPanel, sectionHeading, woodPanel } from '@/shared/ui/game-theme';
import {serviceFailureUncertain,serviceFailureText} from "../utils/npc-service-errors";
import {NPCServiceCommands} from "../utils/npc-service-commands";
import {useNPCServiceQuote} from "../hooks/use-npc-service-quote";
import { useMemo, useState, useEffect, useCallback, useRef, type CSSProperties } from 'react';
import type { NPC, Quest, HeroQuest } from '@/game/types';
import {
  getNPCQuests,
  acceptQuest,
  claimQuest,
  buyPotion,
  buyTownMerchantGear,
  healAtNPC,
  setNPCDialogPause,
  fetchMerchantStock,
} from '@/network/api';
import type { HeroResponse, MerchantGearOfferItem } from '@/network/api';
import { RARITY_COLORS, RARITY_GLOW } from '@/shared/constants';
import { getTelegramUserId } from '@/shared/telegram';
import { hapticImpact } from '@/shared/telegram';
import { useT, t, useLocale } from '@/i18n';
import { localizedQuestText } from '@/i18n/questCopy';
import { npcLabel } from '@/i18n/contentLabels';
import { TownNewsContainer } from '@/modules/town-news/containers/town-news-container';

// ---- Types ----

interface NPCDialogProps {
  npc: NPC;
  heroQuests: HeroQuest[];
  heroGold: number;
  heroId: number;
  heroHP: number;
  heroMaxHP: number;
  heroLevel: number;
  heroPotions: number;
  getHeroWorldPosition: () => { x: number; y: number };
  onClose: () => void;
  onQuestsChanged: () => void;
  onHeroUpdated: (hero: HeroResponse) => void;
  onToast: (message: string, color: string) => void;
  /** Block quest reward claim while hero is dead. */
  questClaimDisabled?: boolean;
  /**
   * When set, notifies the server for ExcursionKindTown: interaction opened on mount,
   * dialog + interaction closed on unmount (WebSocket commands).
   */
  townTourWs?: {
    onInteractionOpened: () => void;
    onDialogAndInteractionClosed: () => void;
  };
}

// ---- Styles ----

const overlayStyle: CSSProperties = {
  position: 'absolute',
  inset: 0,
  zIndex: 700,
  pointerEvents: 'auto',
};

const backdropStyle: CSSProperties = {
  position: 'absolute',
  inset: 0,
  backgroundColor: 'rgba(0, 0, 0, 0.6)',
};

const dialogStyle: CSSProperties = {
  position: 'absolute',
  bottom: 0,
  left: 0,
  right: 0,
  maxHeight: '55vh',

  borderTop: '2px solid rgba(255, 215, 0, 0.35)',

  display: 'flex',
  flexDirection: 'column',
  animation: 'npc-dialog-slide-up 0.3s ease-out',
  overflow: 'hidden',
  ...woodPanel,
};

const headerStyle: CSSProperties = {
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'space-between',
  padding: '12px 16px 8px',
  borderBottom: '1px solid rgba(255, 255, 255, 0.08)',
};

const npcNameStyle: CSSProperties = {
  fontSize: 15,

  color: '#e4d8c4',
  ...sectionHeading,
};

const npcTypeTag: CSSProperties = {
  fontSize: 10,
  fontWeight: 600,
  color: '#bfb2a0',
  textTransform: 'uppercase',
  letterSpacing: 0.5,
  marginLeft: 8,
};

const closeBtnStyle: CSSProperties = {
  background: 'none',
  border: 'none',
  color: '#9f9687',
  fontSize: 20,
  cursor: 'pointer',
  padding: '4px 8px',
  lineHeight: 1,
};

const bodyStyle: CSSProperties = {
  overflowY: 'auto',
  padding: '10px 14px 18px',
  flex: 1,
};

const sectionTitleStyle: CSSProperties = {
  fontSize: 11,

  textTransform: 'uppercase',
  letterSpacing: 0.5,
  marginTop: 10,
  marginBottom: 6,
  ...sectionHeading,
};

const questCardStyle: CSSProperties = {

  padding: '10px 12px',
  marginBottom: 8,
  ...metalPanel,
};

const questTitleRow: CSSProperties = {
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'space-between',
  gap: 8,
};

const questTitleText: CSSProperties = {
  fontSize: 13,
  fontWeight: 600,
  color: '#e4d8c4',
  flex: 1,
};

const questDescStyle: CSSProperties = {
  fontSize: 11,
  color: '#bfb2a0',
  marginTop: 4,
  lineHeight: 1.4,
};

const rewardsRow: CSSProperties = {
  display: 'flex',
  gap: 10,
  marginTop: 6,
  fontSize: 11,
  fontWeight: 600,
};

const rewardChip: CSSProperties = {
  display: 'inline-flex',
  alignItems: 'center',
  gap: 3,
};

const acceptBtnStyle: CSSProperties = {
  marginTop: 8,
  width: '100%',
  padding: '8px 0',

  color: '#8db6c7',
  fontSize: 12,
  fontWeight: 700,
  cursor: 'pointer',
  transition: 'background-color 150ms ease',
  ...copperButton,
};

const claimBtnStyle: CSSProperties = {
  marginTop: 8,
  width: '100%',
  padding: '8px 0',

  color: '#d7b66f',
  fontSize: 12,
  fontWeight: 700,
  cursor: 'pointer',

  ...copperButton,
};

const progressBarOuter: CSSProperties = {
  height: 6,
  borderRadius: 3,
  backgroundColor: 'rgba(255, 255, 255, 0.08)',
  marginTop: 6,
  overflow: 'hidden',
};

const serviceBtnStyle: CSSProperties = {
  width: '100%',
  padding: '12px 0',

  fontSize: 14,
  fontWeight: 700,
  cursor: 'pointer',
  marginBottom: 8,
  transition: 'background-color 150ms ease',
  ...copperButton,
};

const disabledBtnStyle: CSSProperties = {
  ...serviceBtnStyle,
  opacity: 0.4,
  cursor: 'default',
};

// ---- NPC Type Info ----

const GEAR_VENDOR_TYPES = new Set(['merchant', 'armorer', 'weapon', 'jeweler']);
const QUEST_NPC_TYPES = new Set(['quest_giver', 'bounty_hunter', 'elder']);

function isGearVendorType(t: string): boolean {
  return GEAR_VENDOR_TYPES.has(t);
}

function isQuestNPCType(t: string): boolean {
  return QUEST_NPC_TYPES.has(t);
}

function npcTypeIcon(type: string) {
  switch (type) {
    case 'crier':
      return <GameIcon name="megaphone" size={18} color="#ba8b5d" />;
    case 'quest_giver':
    case 'bounty_hunter':
      return <GameIcon name="swords" size={18} color="#ba8b5d" />;
    case 'elder':
      return <GameIcon name="scroll" size={18} color="#ba8b5d" />;
    case 'merchant':
    case 'armorer':
    case 'weapon':
    case 'jeweler':
      return <GameIcon name="coins" size={18} color="#ba8b5d" />;
    case 'healer':
      return <GameIcon name="heart" size={18} color="#ba8b5d" />;
    default:
      return <GameIcon name="megaphone" size={18} color="#ba8b5d" />;
  }
}

function npcTypeLabel(type: string, tr: ReturnType<typeof useT>): string {
  switch (type) {
    case 'crier':
      return tr.crier;
    case 'quest_giver':
      return tr.questGiver;
    case 'bounty_hunter':
      return tr.bountyHunter;
    case 'elder':
      return tr.elder;
    case 'merchant':
      return tr.merchant;
    case 'armorer':
      return tr.armorer;
    case 'weapon':
      return tr.weaponSmith;
    case 'jeweler':
      return tr.jeweler;
    case 'healer':
      return tr.healer;
    default:
      return tr.npc;
  }
}

function questTypeIcon(type: string) {
  switch (type) {
    case 'kill_count': return <GameIcon name="swords" size={18} color="#ba8b5d" />;
    case 'visit_town': return <GameIcon name="map" size={18} color="#ba8b5d" />;
    case 'collect_item': return <GameIcon name="gem" size={18} color="#ba8b5d" />;
    default: return <GameIcon name="scroll" size={18} color="#ba8b5d" />;
  }
}

function prettyEquipmentSlot(slot: string): string {
  return slot
    .split('_')
    .map((p) => (p.length ? p.charAt(0).toUpperCase() + p.slice(1) : p))
    .join(' ');
}

function gearStatLabel(tr: ReturnType<typeof useT>, statType: string): string {
  switch (statType) {
    case 'attack':
      return tr.atk;
    case 'defense':
      return tr.def;
    case 'speed':
      return tr.spd;
    default:
      return tr.stat;
  }
}

function offerRarityStyle(rarity: string): { color: string; textShadow?: string } {
  const r = rarity.toLowerCase();
  const color = RARITY_COLORS[r] ?? '#9d9d9d';
  const glow = RARITY_GLOW[r];
  if (glow && glow !== 'none') {
    return { color, textShadow: glow };
  }
  return { color };
}

// ---- Component ----

export function NPCDialog({
  npc,
  heroQuests,
  heroGold,
  heroId,
  heroHP,
  heroMaxHP,
  heroLevel,
  heroPotions,
  getHeroWorldPosition,
  onClose,
  onQuestsChanged,
  onHeroUpdated,
  onToast,
  questClaimDisabled = false,
  townTourWs,
}: NPCDialogProps) {
  const tr = useT();
  const service = useNPCServiceQuote(heroId,npc.id,heroHP,heroMaxHP,heroLevel,heroPotions,npc.type === "healer" || isGearVendorType(npc.type));
  const serviceCommands = useMemo(()=>new NPCServiceCommands(heroId,npc.id),[heroId,npc.id]);
  const [serviceBusy,setServiceBusy] = useState(false);
  const serviceIdentityRef = useRef(`${heroId}:${npc.id}`); serviceIdentityRef.current = `${heroId}:${npc.id}`;
  const serviceAliveRef = useRef(true);
  useEffect(()=>{serviceAliveRef.current=true;setServiceBusy(false);return()=>{serviceAliveRef.current=false}},[heroId,npc.id]);
  const potionCost = serviceCommands.get("potion")?.expectedGoldCost ?? service.quote?.potionGold ?? service.lastQuote?.potionGold ?? 0;
  const healCost = serviceCommands.get("healing")?.expectedGoldCost ?? service.quote?.healingGold ?? service.lastQuote?.healingGold ?? 0;
  const { locale } = useLocale();
  const npcDisplayName = npcLabel(locale, npc.nameKey, npc.name);
  const [availableQuests, setAvailableQuests] = useState<Quest[]>([]);
  const [loading, setLoading] = useState(false);
  const [merchantOffers, setMerchantOffers] = useState<MerchantGearOfferItem[]>([]);
  const merchantOwnerKey = `${heroId}:${npc.id}`;
  const [merchantOwner,setMerchantOwner]=useState(merchantOwnerKey);
  const displayedMerchantOffers=merchantOwner===merchantOwnerKey?merchantOffers:[];
  const [merchantStockLoading, setMerchantStockLoading] = useState(false);
  const [merchantStockError, setMerchantStockError] = useState(false);

  const telegramId = getTelegramUserId() ?? 1;
  const townTourWsRef = useRef(townTourWs);
  townTourWsRef.current = townTourWs;

  // Pause town tour timers while any NPC dialog (shop / quests / healer) is open.
  useEffect(() => {
    let cancelled = false;
    setNPCDialogPause(true, telegramId).catch((err) => {
      if (!cancelled) console.warn('[NPCDialog] npc-dialog-pause (open) failed:', err);
    });
    townTourWsRef.current?.onInteractionOpened();
    return () => {
      cancelled = true;
      setNPCDialogPause(false, telegramId).catch((err) => {
        console.warn('[NPCDialog] npc-dialog-pause (close) failed:', err);
      });
      townTourWsRef.current?.onDialogAndInteractionClosed();
    };
  }, [telegramId]);

  // Gear vendors: roll stock when the shop opens (server-tier gear for this town).
  useEffect(() => {
    if (!isGearVendorType(npc.type)) return;
    let cancelled = false;
    setMerchantOwner(merchantOwnerKey);setMerchantOffers([]);
    setMerchantStockLoading(true);
    setMerchantStockError(false);
    const pos = getHeroWorldPosition();
    fetchMerchantStock({ npcId: npc.id, positionX: pos.x, positionY: pos.y }, telegramId)
      .then((res) => {
        if (cancelled) return;
        setMerchantOffers(Array.isArray(res.items) ? res.items : []);
      })
      .catch((err) => {
        if (cancelled) return;
        console.warn('[NPCDialog] merchant stock failed:', err);
        setMerchantStockError(true);
        setMerchantOffers([]);
      })
      .finally(() => {
        if (!cancelled) setMerchantStockLoading(false);
      });
    return () => {
      cancelled = true;
    };
    // Intentionally depend on npc identity only — position is read once when the dialog opens.
    // eslint-disable-next-line react-hooks/exhaustive-deps -- getHeroWorldPosition may be unstable from parent
  }, [heroId,npc.id, npc.type, telegramId,merchantOwnerKey]);

  // Fetch available quests for bounty / elder (and legacy quest_giver).
  useEffect(() => {
    if (!isQuestNPCType(npc.type)) return;
    setLoading(true);
    getNPCQuests(npc.id, telegramId)
      .then((qs) => setAvailableQuests(Array.isArray(qs) ? qs : []))
      .catch((err) => {
        console.warn('[NPCDialog] Failed to fetch quests:', err);
        setAvailableQuests([]);
      })
      .finally(() => setLoading(false));
  }, [heroId,npc.id, npc.type, telegramId,merchantOwnerKey]);

  // Close on Escape
  useEffect(() => {
    const handleKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape') onClose();
    };
    window.addEventListener('keydown', handleKey);
    return () => window.removeEventListener('keydown', handleKey);
  }, [onClose]);

  const handleAcceptQuest = useCallback(
    (questId: number) => {
      acceptQuest(questId, telegramId)
        .then(() => {
          hapticImpact('medium');
          onToast(tr.questAccepted, '#8db6c7');
          onQuestsChanged();
          // Remove from available list
          setAvailableQuests((prev) => (Array.isArray(prev) ? prev : []).filter((q) => q.id !== questId));
        })
        .catch((err) => {
          console.warn('[NPCDialog] Failed to accept quest:', err);
          onToast(tr.failedToAcceptQuest, '#cf9686');
        });
    },
    [telegramId, onQuestsChanged, onToast],
  );

  const handleClaimQuest = useCallback(
    (heroQuestId: number) => {
      claimQuest(heroQuestId, telegramId)
        .then((hero) => {
          hapticImpact('heavy');
          onToast(tr.questRewardsClaimed, '#d7b66f');
          onHeroUpdated(hero);

          onQuestsChanged();
        })
        .catch((err) => {
          console.warn('[NPCDialog] Failed to claim quest:', err);
          onToast(tr.failedToClaimRewards, '#cf9686');
        });
    },
    [telegramId, onQuestsChanged, onHeroUpdated, onToast],
  );

  const handleBuyPotion = useCallback(() => {
    if (serviceBusy || (!serviceCommands.get("potion") && (!service.quote || !service.quote.potionAvailable))) return;
    if (!serviceCommands.get("potion") && heroGold < potionCost) {
      onToast(tr.notEnoughGold, '#cf9686');
      return;
    }
    const pos = getHeroWorldPosition();
    const requestOwner = `${heroId}:${npc.id}`;
    setServiceBusy(true);
    let command;
    try { command = serviceCommands.prepare("potion", {npcId:npc.id,positionX:pos.x,positionY:pos.y,expectedGoldCost:potionCost}); }
    catch { setServiceBusy(false); onToast(locale === "ru" ? "Подтвердите предыдущие операции." : "Confirm earlier pending requests.", "#cf9686"); return; }
    buyPotion(command, telegramId)
      .then((hero) => {
        serviceCommands.settled("potion",command.commandId);
        if(!serviceAliveRef.current || serviceIdentityRef.current!==requestOwner)return;
        hapticImpact('medium');

        onToast(t(tr.boughtPotion, { cost: potionCost }), '#9bb58a');
        onHeroUpdated(hero);
        service.reload();
      })
      .catch((err) => {
        if (!serviceFailureUncertain(err)) serviceCommands.settled("potion",command.commandId);
        if(!serviceAliveRef.current || serviceIdentityRef.current!==requestOwner)return;
        if (!serviceFailureUncertain(err)) service.reload();
        onToast(serviceFailureText(err,locale,tr.failedToBuyPotion), '#cf9686');
      })
      .finally(()=>{if(serviceAliveRef.current && serviceIdentityRef.current===requestOwner)setServiceBusy(false)});
  }, [locale,heroId,serviceCommands, serviceBusy,service, telegramId, heroGold, potionCost, npc.id, getHeroWorldPosition, onHeroUpdated, onToast, tr]);

  const pendingGear = serviceCommands.pendingMerchant();
  const performGearPurchase = useCallback((command: ReturnType<typeof serviceCommands.prepareMerchant>) => {
    if(serviceBusy)return;
    const requestOwner=`${heroId}:${npc.id}`;
    setServiceBusy(true);
    buyTownMerchantGear(command,telegramId).then(hero=>{
      serviceCommands.settledMerchant(command.offerId,command.commandId);
      if(!serviceAliveRef.current||serviceIdentityRef.current!==requestOwner)return;
      hapticImpact('medium');onHeroUpdated(hero);
      onToast(t(tr.boughtGearFromMerchant,{cost:command.expectedGoldCost}),'#c9a227');
      setMerchantOffers(prev=>prev.filter(row=>row.offerId!==command.offerId));
    }).catch(err=>{
      if(!serviceFailureUncertain(err))serviceCommands.settledMerchant(command.offerId,command.commandId);
      if(!serviceAliveRef.current||serviceIdentityRef.current!==requestOwner)return;
      if(!serviceFailureUncertain(err)){
        service.reload();
        const position=getHeroWorldPosition();setMerchantStockLoading(true);
        fetchMerchantStock({npcId:npc.id,positionX:position.x,positionY:position.y},telegramId).then(stock=>{
          if(serviceAliveRef.current&&serviceIdentityRef.current===requestOwner){setMerchantOffers(Array.isArray(stock.items)?stock.items:[]);setMerchantStockError(false)}
        }).catch(()=>{if(serviceAliveRef.current&&serviceIdentityRef.current===requestOwner){setMerchantOffers([]);setMerchantStockError(true)}}).finally(()=>{if(serviceAliveRef.current&&serviceIdentityRef.current===requestOwner)setMerchantStockLoading(false)});
      }
      onToast(serviceFailureText(err,locale,tr.failedToBuyGear),'#cf9686');
    }).finally(()=>{if(serviceAliveRef.current&&serviceIdentityRef.current===requestOwner)setServiceBusy(false)});
  },[serviceBusy,heroId,npc.id,telegramId,serviceCommands,onHeroUpdated,onToast,tr,locale,service,getHeroWorldPosition]);
  const handleBuyTownGear = useCallback((offerIndex:number)=>{
    if(serviceBusy||!service.quote?.merchantAllowed||serviceCommands.pendingMerchant().length>0)return;
    const row=merchantOffers[offerIndex];
    if(!row||!Number.isSafeInteger(row.cost)||row.cost<=0){onToast(tr.failedToBuyGear,'#cf9686');return}
    if(heroGold<row.cost){onToast(tr.notEnoughGold,'#cf9686');return}
    const pos=getHeroWorldPosition();
    if(!row.offerId){
 const requestOwner=`${heroId}:${npc.id}`;setServiceBusy(true);
 buyTownMerchantGear({npcId:npc.id,positionX:pos.x,positionY:pos.y,offerIndex},telegramId).then(hero=>{
  if(!serviceAliveRef.current||serviceIdentityRef.current!==requestOwner)return;
  onHeroUpdated(hero);onToast(t(tr.boughtGearFromMerchant,{cost:row.cost}),'#c9a227');setMerchantOffers(prev=>prev.filter((_,i)=>i!==offerIndex));
 }).catch(()=>{if(serviceAliveRef.current&&serviceIdentityRef.current===requestOwner)onToast(tr.failedToBuyGear,'#cf9686')}).finally(()=>{if(serviceAliveRef.current&&serviceIdentityRef.current===requestOwner)setServiceBusy(false)});
 return;
}
try{performGearPurchase(serviceCommands.prepareMerchant({npcId:npc.id,positionX:pos.x,positionY:pos.y,offerIndex,offerId:row.offerId,expectedGoldCost:row.cost}))}
    catch{onToast(tr.failedToBuyGear,'#cf9686')}
  },[serviceBusy,service.quote,serviceCommands,merchantOffers,heroGold,getHeroWorldPosition,performGearPurchase,npc.id,onToast,tr,heroId,telegramId,onHeroUpdated]);
  const handleHeal = useCallback(() => {
    if (serviceBusy || (!serviceCommands.get("healing") && !service.quote)) return;
    if (!serviceCommands.get("healing") && heroGold < healCost) {
      onToast(tr.notEnoughGold, '#cf9686');
      return;
    }
    const pos = getHeroWorldPosition();
    const requestOwner = `${heroId}:${npc.id}`;
    setServiceBusy(true);
    let command;
    try { command = serviceCommands.prepare("healing", {npcId:npc.id,positionX:pos.x,positionY:pos.y,expectedGoldCost:healCost}); }
    catch { setServiceBusy(false); onToast(locale === "ru" ? "Подтвердите предыдущие операции." : "Confirm earlier pending requests.", "#cf9686"); return; }
    healAtNPC(command, telegramId)
      .then((hero) => {
        serviceCommands.settled("healing",command.commandId);
        if(!serviceAliveRef.current || serviceIdentityRef.current!==requestOwner)return;
        hapticImpact('medium');

        onToast(tr.healedToFull, '#44cc44');
        onHeroUpdated(hero);
        service.reload();
      })
      .catch((err) => {
        if (!serviceFailureUncertain(err)) serviceCommands.settled("healing",command.commandId);
        if(!serviceAliveRef.current || serviceIdentityRef.current!==requestOwner)return;
        if (!serviceFailureUncertain(err)) service.reload();
        onToast(serviceFailureText(err,locale,tr.failedToHeal), '#cf9686');
      })
      .finally(()=>{if(serviceAliveRef.current && serviceIdentityRef.current===requestOwner)setServiceBusy(false)});
  }, [locale,heroId,serviceCommands, serviceBusy,service, telegramId, heroGold, healCost, npc.id, getHeroWorldPosition, onHeroUpdated, onToast, tr]);

  // Quests relevant to this NPC
  const npcHeroQuests = heroQuests.filter(
    (hq) => hq.status !== 'claimed',
  );
  // Filter to quests from this NPC based on npcName matching
  const npcInProgressQuests = npcHeroQuests.filter(
    (hq) => hq.questNpcId === npc.id && hq.status === 'accepted',
  );
  const npcCompletedQuests = npcHeroQuests.filter(
    (hq) => hq.questNpcId === npc.id && hq.status === 'completed',
  );

  return (
    <>
      <style>{`
        @keyframes npc-dialog-slide-up {
          0%   { transform: translateY(100%); }
          100% { transform: translateY(0); }
        }
        @keyframes npc-claim-glow {
          0%, 100% { box-shadow: 0 0 8px rgba(255, 215, 0, 0.15); }
          50%      { box-shadow: 0 0 18px rgba(255, 215, 0, 0.4); }
        }
      `}</style>
      <div style={overlayStyle}>
        <div style={backdropStyle} onClick={onClose} />
        <div style={dialogStyle}>
          {/* Header */}
          <div style={headerStyle}>
            <div style={{ display: 'flex', alignItems: 'center' }}>
              <span style={{ fontSize: 18, marginRight: 8 }}>{npcTypeIcon(npc.type)}</span>
              <span style={npcNameStyle}>{npcDisplayName}</span>
              <span style={npcTypeTag}>{npcTypeLabel(npc.type, tr)}</span>
            </div>
            <button style={closeBtnStyle} onClick={onClose}><GameIcon name="close" size={15} /></button>
          </div>

          {/* Body */}
          <div style={bodyStyle}>
            {npc.type === 'crier' && <TownNewsContainer key={npc.id} npcId={npc.id} language={locale} />}
            {/* ---- Quest NPCs ---- */}
            {isQuestNPCType(npc.type) && (
              <>
                {/* Completed quests — claim first */}
                {npcCompletedQuests.length > 0 && (
                  <>
                    <div style={sectionTitleStyle}>{tr.completed}</div>
                    {npcCompletedQuests.map((hq) => (
                      <div
                        key={hq.id}
                        style={{
                          ...questCardStyle,
                          borderColor: 'rgba(255, 215, 0, 0.3)',
                        }}
                      >
                        <div style={questTitleRow}>
                          <span style={{ fontSize: 14 }}>{questTypeIcon(hq.type)}</span>
                          <span style={questTitleText}>
                            {localizedQuestText(locale, hq.questKey, 'title', hq.title)}
                          </span>
                          <span style={{ fontSize: 11, fontWeight: 700, color: '#d7b66f' }}>
                            {hq.progress}/{hq.targetCount}
                          </span>
                        </div>
                        <div style={rewardsRow}>
                          {hq.rewardXp > 0 && (
                            <span style={{ ...rewardChip, color: '#8db6c7' }}>
                              <GameIcon name="sparkles" size={15} /> {hq.rewardXp} XP
                            </span>
                          )}
                          {hq.rewardGold > 0 && (
                            <span style={{ ...rewardChip, color: '#d7b66f' }}>
                              <GameIcon name="coins" size={15} /> {hq.rewardGold}
                            </span>
                          )}
                          {hq.rewardPotions > 0 && (
                            <span style={{ ...rewardChip, color: '#9bb58a' }}>
                              <GameIcon name="flask" size={15} /> {hq.rewardPotions}
                            </span>
                          )}
                        </div>
                        <button
                          type="button"
                          style={questClaimDisabled ? { ...claimBtnStyle, opacity: 0.45, cursor: 'not-allowed', animation: 'none' } : claimBtnStyle}
                          disabled={questClaimDisabled}
                          title={questClaimDisabled ? tr.claimRewardsDisabledDead : undefined}
                          onClick={() => {
                            if (questClaimDisabled) return;
                            handleClaimQuest(hq.id);
                          }}
                        >
                          {tr.claimRewards}
                        </button>
                      </div>
                    ))}
                  </>
                )}

                {/* In-progress quests */}
                {npcInProgressQuests.length > 0 && (
                  <>
                    <div style={sectionTitleStyle}>{tr.inProgressQuests}</div>
                    {npcInProgressQuests.map((hq) => {
                      const pct = hq.targetCount > 0
                        ? Math.min(100, (hq.progress / hq.targetCount) * 100)
                        : 0;
                      return (
                        <div key={hq.id} style={questCardStyle}>
                          <div style={questTitleRow}>
                            <span style={{ fontSize: 14 }}>{questTypeIcon(hq.type)}</span>
                            <span style={questTitleText}>
                            {localizedQuestText(locale, hq.questKey, 'title', hq.title)}
                          </span>
                            <span style={{ fontSize: 11, fontWeight: 700, color: '#bfb2a0' }}>
                              {hq.progress}/{hq.targetCount}
                            </span>
                          </div>
                          <div style={progressBarOuter}>
                            <div
                              style={{
                                height: '100%',
                                width: `${pct}%`,
                                borderRadius: 3,
                                backgroundColor: '#4a90d9',
                                transition: 'width 300ms ease',
                              }}
                            />
                          </div>
                        </div>
                      );
                    })}
                  </>
                )}

                {/* Available quests */}
                {loading ? (
                  <div style={{ color: '#9f9687', fontSize: 12, textAlign: 'center', padding: 16 }}>
                    {tr.loadingQuests}
                  </div>
                ) : (availableQuests?.length ?? 0) > 0 ? (
                  <>
                    <div style={sectionTitleStyle}>{tr.availableQuestsSection}</div>
                    {(availableQuests ?? []).map((q) => (
                      <div key={q.id} style={questCardStyle}>
                        <div style={questTitleRow}>
                          <span style={{ fontSize: 14 }}>{questTypeIcon(q.type)}</span>
                          <span style={questTitleText}>
                            {localizedQuestText(locale, q.questKey, 'title', q.title)}
                          </span>
                        </div>
                        <div style={questDescStyle}>
                          {localizedQuestText(locale, q.questKey, 'description', q.description)}
                        </div>
                        <div style={rewardsRow}>
                          {q.rewardXp > 0 && (
                            <span style={{ ...rewardChip, color: '#8db6c7' }}>
                              <GameIcon name="sparkles" size={15} /> {q.rewardXp} XP
                            </span>
                          )}
                          {q.rewardGold > 0 && (
                            <span style={{ ...rewardChip, color: '#d7b66f' }}>
                              <GameIcon name="coins" size={15} /> {q.rewardGold}
                            </span>
                          )}
                          {q.rewardPotions > 0 && (
                            <span style={{ ...rewardChip, color: '#9bb58a' }}>
                              <GameIcon name="flask" size={15} /> {q.rewardPotions}
                            </span>
                          )}
                        </div>
                        <button
                          style={acceptBtnStyle}
                          onClick={() => handleAcceptQuest(q.id)}
                        >
                          {tr.acceptQuest}
                        </button>
                      </div>
                    ))}
                  </>
                ) : (
                  npcInProgressQuests.length === 0 &&
                  npcCompletedQuests.length === 0 && (
                    <div style={{ color: '#9f9687', fontSize: 12, textAlign: 'center', padding: 16 }}>
                      {tr.noQuestsRightNow}
                    </div>
                  )
                )}
              </>
            )}

            {/* ---- Gear vendors ---- */}
            {isGearVendorType(npc.type) && (
              <>
                <div style={sectionTitleStyle}>{tr.shopLabel}</div>
                {pendingGear.map(command=><button key={command.commandId} type="button" style={serviceBusy?disabledBtnStyle:serviceBtnStyle} disabled={serviceBusy} onClick={()=>performGearPurchase(command)}>{locale==='ru'?'Подтвердить прежнюю покупку':'Confirm previous purchase'}</button>)}
{merchantStockLoading ? (
                  <div style={{ color: '#9f9687', fontSize: 12, textAlign: 'center', padding: 16 }}>
                    {tr.merchantStockLoading}
                  </div>
                ) : service.failed ? (
<p role="status">{locale === "ru" ? "Не удалось подтвердить условия торговли." : "Trading conditions could not be confirmed."}</p>
) : service.quote && !service.quote.merchantAllowed ? (
<p role="status">{locale === "ru" ? "Торговец отказывается обслуживать разыскиваемого героя." : "The merchant refuses to trade with a wanted hero."}</p>
) : merchantStockError ? (
                  <div style={{ color: '#c66', fontSize: 12, textAlign: 'center', padding: 16 }}>
                    {tr.merchantStockFailed}
                  </div>
                ) : displayedMerchantOffers.length === 0 ? (
                  <div style={{ color: '#9f9687', fontSize: 12, textAlign: 'center', padding: 16 }}>
                    {tr.merchantEmptyStock}
                  </div>
                ) : (
                  displayedMerchantOffers.map((item, offerIndex) => {
                    const rc = offerRarityStyle(item.rarity);
                    const critPct = Math.round((item.critChance ?? 0) * 100);
                    const spd = typeof item.speedModifier === 'number' ? item.speedModifier.toFixed(2) : '?';
                    const rowCost = typeof item.cost === 'number' ? item.cost : 0;
                    const canBuy = !serviceBusy && pendingGear.length===0 && !!service.quote?.merchantAllowed && rowCost > 0 && heroGold >= rowCost;
                    return (
                      <div
                        key={`${item.formId}-${offerIndex}-${item.name}`}
                        style={{
                          ...questCardStyle,
                          borderColor: 'rgba(200, 180, 80, 0.2)',
                        }}
                      >
                        <div style={questTitleRow}>
                          <ItemArt item={item} size={44} />
                          <span style={{ ...questTitleText, ...rc }}>{item.name}</span>
                        </div>
                        <div style={{ fontSize: 11, color: '#9f9687', marginTop: 2 }}>
                          {item.rarity.charAt(0).toUpperCase() + item.rarity.slice(1)} · ilvl {item.ilvl} ·{' '}
                          {prettyEquipmentSlot(item.slot)}
                          {item.subtype ? ` · ${item.subtype}` : ''}
                        </div>
                        <div style={{ ...questDescStyle, marginTop: 6 }}>
                          {gearStatLabel(tr, item.statType)} {item.primaryStat}
                          {' · '}
                          crit {critPct}%
                          {' · '}
                          {tr.spd} ×{spd}
                          {(item.agilityBonus ?? 0) > 0 ? ` · +${item.agilityBonus} AGI` : ''}
                        </div>
                        <button
                          type="button"
                          style={
                            canBuy
                              ? { ...serviceBtnStyle, backgroundColor: 'rgba(200, 180, 80, 0.2)', color: '#d7b66f' }
                              : { ...disabledBtnStyle, backgroundColor: 'rgba(200, 180, 80, 0.1)', color: '#d7b66f' }
                          }
                          onClick={() => handleBuyTownGear(offerIndex)}
                          disabled={!canBuy}
                        >
                          {rowCost > 0 ? t(tr.buyGearForGold, { cost: rowCost }) : tr.failedToBuyGear}
                        </button>
                      </div>
                    );
                  })
                )}
                <div style={{ fontSize: 11, color: '#9f9687', textAlign: 'center', marginTop: 6 }}>
                  {t(tr.yourGoldLabel, { amount: heroGold })}
                </div>
              </>
            )}

            {/* ---- Healer ---- */}
            {npc.type === 'healer' && (
              <>
                <div style={sectionTitleStyle}>{tr.servicesSection}</div>
{service.failed && <p role="status">{locale === "ru" ? "Не удалось подтвердить цену услуг." : "Service prices could not be confirmed."}</p>}
                <button
                  style={
                    heroGold >= potionCost
                      ? { ...serviceBtnStyle, backgroundColor: 'rgba(68, 200, 68, 0.2)', color: '#9bb58a' }
                      : { ...disabledBtnStyle, backgroundColor: 'rgba(68, 200, 68, 0.1)', color: '#9bb58a' }
                  }
                  onClick={handleBuyPotion}
                  disabled={serviceBusy || (!serviceCommands.get("potion") && (!service.quote || !service.quote.potionAvailable || heroGold < potionCost))}
                >
                  <GameIcon name="flask" size={15} /> {serviceCommands.get("potion") ? (locale === "ru" ? "Подтвердить покупку" : "Confirm purchase") : tr.buyPotion} &mdash; {service.quote || service.lastQuote ? potionCost : "…"} {tr.gold}
                </button>
                <button
                  style={
                    heroGold >= healCost
                      ? { ...serviceBtnStyle, backgroundColor: 'rgba(200, 68, 68, 0.2)', color: '#cf9686' }
                      : { ...disabledBtnStyle, backgroundColor: 'rgba(200, 68, 68, 0.1)', color: '#cf9686' }
                  }
                  onClick={handleHeal}
                  disabled={serviceBusy || (!serviceCommands.get("healing") && (!service.quote || heroGold < healCost))}
                >
                  {'\u2764\uFE0F'} {serviceCommands.get("healing") ? (locale === "ru" ? "Подтвердить лечение" : "Confirm healing") : tr.healToFull} &mdash; {service.quote || service.lastQuote ? healCost : "…"} {tr.gold}
                </button>
                <div style={{ fontSize: 11, color: '#9f9687', textAlign: 'center' }}>
                  {t(tr.yourGoldLabel, { amount: heroGold })}
                </div>
              </>
            )}
          </div>
        </div>
      </div>
    </>
  );
}
